import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useCoinList } from '../../hooks/useCoinList';
import { useCoinDetail } from '../../hooks/useCoinDetail';
import { TradingChart, ChartView, CandleInterval, PriceLineOverlay } from './TradingChart';
import { useCurrency } from '../../context/CurrencyContext';
import { usePortfolioContext } from '../../context/PortfolioContext';
import { useStrategy } from '../../context/StrategyContext';
import { AssetComparison, AssetOption } from '../Strategy/AssetComparison';
import { CoinTransactionHistory } from './CoinTransactionHistory';
import { CoinInfo } from './CoinInfo';
import { StockChart, StockRange } from './StockChart';
import { StockInfo } from './StockInfo';
import { getAssetType, isStockOrETF, tickerFromId, SUPPORTED_STOCKS } from '../../utils/stocks';
import { useStockMarket } from '../../hooks/useStockMarket';
import { Footer } from '../Layout/Footer';
import { CurrencySelector } from '../Layout/CurrencySelector';
import { ThemeToggle } from '../Layout/ThemeToggle';
import { currencySymbol, formatAmount, formatPercent } from '../../utils/formatters';
import {
  ArrowLeft, TrendingUp, TrendingDown,
  LineChart as LineIcon, CandlestickChart as CandleIcon,
} from 'lucide-react';

const VIEW_KEY  = 'mycryptoplanner_chart_view';

// Candle-interval controls. Labels are the candle PERIOD. 1H and 4H cover the
// last ~90 days (hourly OHLC, fetched when first opened); 1D and 1W the ~2-year
// daily dataset. The interval is not persisted; TradingChart normalises whatever
// it is given, so a stale 'monthly' would render as 1D.
const INTERVALS: { key: CandleInterval; label: string }[] = [
  { key: 'hourly', label: '1H' },
  { key: '4h',     label: '4H' },
  { key: 'daily',  label: '1D' },
  { key: 'weekly', label: '1W' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function loadView(): ChartView {
  try { return (localStorage.getItem(VIEW_KEY) as ChartView) || 'candle'; }
  catch { return 'candle'; }
}

// ── Plan summary helpers ──────────────────────────────────────────────────────

// Distinct palette for wallets/exchanges, mirrored from WalletAllocationChart.
// First slot is bronze (replacing the old bright emerald) so the "Allocation By
// Wallet" bar's leading colour matches the new design system's framing accent.
const WALLET_PALETTE = [
  '#d4b483', '#6b95cf', '#f5b97a', '#06b6d4', '#ec4899',
  '#c4615e', '#84cc16', '#f97316', '#14b8a6', '#38bdf8',
  '#a0a0a0', '#6b7280',
];

// Palettes for chart tier-line overlays — buy in blue/green (entry), sell in red/orange (exit)
const BUY_LINE_COLORS  = [
  '#d4b483', // bronze (Tier 1)
  '#6b95cf', // darkened blue (Tier 2)
  '#a86fb8', // darkened purple (Tier 3)
  '#0ea5e9', // sky-500
  '#22d3ee', // cyan-400
  '#14b8a6', // teal-500
  '#34d399', // emerald-400
  '#60a5fa', // blue-400
  '#2dd4bf', // teal-400
  '#0d9488', // teal-700
];
const SELL_LINE_COLORS = [
  '#c4615e', // muted coral (Tier 1)
  '#f97316', // orange-500
  '#f59e0b', // amber-500
  '#fbbf24', // amber-400
  '#facc15', // yellow-400
  '#fb923c', // orange-400
  '#dc2626', // red-600
  '#ea580c', // orange-600
  '#f87171', // red-400
  '#eab308', // yellow-500
];

// Mirrors SplitBuyChart.tsx so chart-line labels match the Strategy tab format exactly
function fmtLabelPrice(v: number, code: string): string {
  const s = currencySymbol(code);
  if (v >= 1_000_000) return `${s}${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `${s}${(v / 1_000).toFixed(1)}k`;
  if (v >= 1)         return `${s}${v.toFixed(2)}`;
  if (v > 0)          return `${s}${v.toFixed(4)}`;
  return `${s}0`;
}

function fmtCoins(n: number): string {
  if (!isFinite(n)) return '—';
  return n >= 0.001
    ? n.toLocaleString('en-US', { maximumFractionDigits: 6 })
    : n.toExponential(4);
}

// ── Main component ────────────────────────────────────────────────────────────

export function CoinChartPage() {
  const { coinId }                      = useParams<{ coinId: string }>();
  const navigate                        = useNavigate();
  const location                        = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const locationState = location.state as { from?: string; fromSearch?: string } | null;
  const [candleInterval, setCandleInterval] = useState<CandleInterval>('daily');
  // Stock/ETF chart range (line-only Yahoo path — see the isStock branch below).
  const [stockRange, setStockRange] = useState<StockRange>('3M');

  // Always open a Coin Detail page at the top. Covers fresh loads / refreshes
  // (where the route doesn't change, so the global ScrollToTop wouldn't fire)
  // and navigation between coins. Keyed on coinId so switching coins re-scrolls.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [coinId]);

  const viewParam = searchParams.get('view') as ChartView | null;
  const [view, setViewState] = useState<ChartView>(viewParam ?? loadView());

  // Local, per-visit content tab below the chart. Stocks have no Compare tab, so
  // they default to History (Compare is the crypto/metal default, unchanged).
  const [contentTab, setContentTab] = useState<'compare' | 'history' | 'info'>(
    isStockOrETF(coinId ?? '') ? 'history' : 'compare'
  );

  const { fmt, fmtDisplay, rate, rates, currency } = useCurrency();
  const { coins } = useCoinList();
  const { holdings, transactions } = usePortfolioContext();
  const { buyPlan, sellPlan, taxHarvest } = useStrategy();

  const id   = coinId ?? '';
  const coin = coins.find((c) => c.id === id);
  const isCrypto = getAssetType(id) === 'crypto'; // Info tab is crypto-only

  // ── Stock/ETF branch (additive). isStock gates a separate Yahoo-backed path;
  //    the crypto path below is untouched when isStock is false. ──
  const isStock   = isStockOrETF(id);
  const stockMeta = isStock ? SUPPORTED_STOCKS.find((s) => s.id === id) : undefined;
  // No-op for crypto (empty array → the hook fetches nothing).
  const { data: stockMarketMap, loading: stockLoading } = useStockMarket(isStock ? [id] : []);
  const stockEntry = isStock ? stockMarketMap[id] : undefined;

  // Skip the (doomed) CoinGecko /coins/{id} call for stocks; crypto passes id unchanged.
  const { detail, loading: detailLoading } = useCoinDetail(isStock ? '' : id);

  const currentPrice  = isStock
    ? (stockEntry?.usd ?? 0)
    : (detail?.currentPrice || coin?.currentPrice || 0);
  const change24h     = isStock
    ? (stockEntry?.usd_24h_change ?? 0)
    : (detail?.priceChangePercent24h ?? 0);
  const change24hPos  = change24h >= 0;

  // A stock can't sit on the Compare tab (it's hidden) — bounce to History if the
  // asset type flips to stock while Compare was selected. No-op for crypto/metal.
  useEffect(() => {
    if (isStock && contentTab === 'compare') setContentTab('history');
  }, [isStock, contentTab]);

  const holding             = holdings.find((h) => h.coinId === id) ?? null;
  const matchingBuyPlan     = buyPlan?.coinId    === id ? buyPlan    : null;
  const matchingSellPlan    = sellPlan?.coinId   === id ? sellPlan   : null;
  const matchingTaxHarvest  = taxHarvest?.coinId === id ? taxHarvest : null;

  // Default Coin A for the compact Compare-Assets accordion below. Derived from
  // the coin currently being viewed; user can still pick a different asset.
  const compareDefaultA = useMemo<AssetOption | undefined>(() => (
    coin ? { id: coin.id, name: coin.name, symbol: coin.symbol, assetType: 'crypto', image: coin.image } : undefined
  ), [coin]);

  // This coin's transactions (buys/sells/transfers), newest first. A filtered
  // view of data the app already holds — no refetch, no recompute.
  const coinTransactions = useMemo(
    () => transactions.filter((t) => t.coinId === id).sort((a, b) => b.timestamp - a.timestamp),
    [transactions, id]
  );

  // Build combined Buy + Sell tier price lines for the trading chart.
  // Plan prices are stored in plan.currencyCode. The chart's scale is the DISPLAY
  // currency, so each line is placed by converting the plan price into it (via
  // USD at today's rates), while the label keeps the user-entered value in the
  // plan's own currency so it matches the Strategy tab exactly: "Buy Tier 1 - A$1.20".
  const tierPriceLines = useMemo<PriceLineOverlay[]>(() => {
    const lines: PriceLineOverlay[] = [];
    if (rate <= 0) return lines;
    const toDisplay = (price: number, planCode: string): number | null => {
      if (planCode === currency.code) return price;
      const planPerUsd = planCode === 'USD' ? 1 : rates[planCode];
      if (!planPerUsd || planPerUsd <= 0) return null; // no rate: better no line than a misplaced one
      return (price / planPerUsd) * rate;
    };
    const addTiers = (
      plan: { currencyCode?: string; tiers: { targetPrice: string }[] },
      kind: 'Buy' | 'Sell',
      colors: string[]
    ) => {
      const planCode = plan.currencyCode || currency.code;
      plan.tiers.forEach((t, i) => {
        const inputPrice = parseFloat(t.targetPrice) || 0;
        if (inputPrice <= 0) return;
        const price = toDisplay(inputPrice, planCode);
        if (price === null) return;
        lines.push({
          price,
          color: colors[i % colors.length],
          title: `${kind} Tier ${i + 1} - ${fmtLabelPrice(inputPrice, planCode)}`,
        });
      });
    };
    if (matchingBuyPlan) addTiers(matchingBuyPlan, 'Buy', BUY_LINE_COLORS);
    if (matchingSellPlan) addTiers(matchingSellPlan, 'Sell', SELL_LINE_COLORS);
    return lines;
  }, [matchingBuyPlan, matchingSellPlan, rate, rates, currency.code]);

  // Per-wallet allocation for the current coin, derived from raw transactions.
  // Transfers move coins between wallets without changing total holdings.
  const walletAllocations = React.useMemo(() => {
    if (!id) return [] as { wallet: string; amount: number; valueUsd: number; percent: number; color: string }[];
    const totals: Record<string, number> = {};
    const bump = (w: string, delta: number) => { totals[w] = (totals[w] ?? 0) + delta; };
    for (const tx of transactions) {
      if (tx.coinId !== id) continue;
      if (tx.type === 'TRANSFER') {
        if (tx.fromWallet) bump(tx.fromWallet, -tx.amount);
        if (tx.toWallet)   bump(tx.toWallet,    tx.amount);
        continue;
      }
      const wallet = tx.wallet?.trim() || 'Unspecified';
      bump(wallet, tx.type === 'BUY' ? tx.amount : -tx.amount);
    }
    const positive = Object.entries(totals)
      .filter(([, amt]) => amt > 0.000001)
      .sort((a, b) => b[1] - a[1]);
    const totalAmount = positive.reduce((s, [, amt]) => s + amt, 0);
    return positive.map(([wallet, amount], i) => ({
      wallet,
      amount,
      valueUsd: amount * currentPrice,
      percent:  totalAmount > 0 ? (amount / totalAmount) * 100 : 0,
      color:    WALLET_PALETTE[i % WALLET_PALETTE.length],
    }));
  }, [id, transactions, currentPrice]);

  const totalWalletAmount = walletAllocations.reduce((s, w) => s + w.amount, 0);
  const totalWalletValue  = walletAllocations.reduce((s, w) => s + w.valueUsd, 0);

  function setView(v: ChartView) {
    setViewState(v);
    setSearchParams({ view: v }, { replace: true });
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* ignore */ }
  }

  return (
    <div className="min-h-screen bg-slate-900">

      {/* ── Top bar ── */}
      <div className="border-b border-slate-700 bg-slate-950/95 backdrop-blur-sm sticky top-0 z-30 px-4 md:px-8 min-h-16 md:h-16 flex items-center gap-3 md:gap-5">

        <button
          onClick={() => {
            // Prefer the full return search (e.g. ?tab=market&mktPage=3) so paged
            // views restore exactly; fall back to the tab, then to history.
            if (locationState?.fromSearch) navigate({ pathname: '/app', search: locationState.fromSearch });
            else if (locationState?.from) navigate({ pathname: '/app', search: `?tab=${locationState.from}` });
            else navigate(-1);
          }}
          className="flex items-center gap-1.5 text-slate-400 hover:text-slate-100 transition-colors text-sm font-medium flex-shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back</span>
        </button>

        {/* Coin identity */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {isStock ? (
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: getAssetType(id) === 'etf' ? '#8B5CF615' : '#3B82F615',
                border: `1.5px solid ${getAssetType(id) === 'etf' ? '#8B5CF640' : '#3B82F640'}`,
              }}
            >
              <TrendingUp style={{ color: getAssetType(id) === 'etf' ? '#8B5CF6' : '#3B82F6', width: '50%', height: '50%' }} />
            </div>
          ) : (
            coin?.image && (
              <img src={coin.image} alt={coin.name} className="w-8 h-8 rounded-full flex-shrink-0" />
            )
          )}
          <div className="min-w-0 flex items-baseline gap-2 md:gap-2.5">
            <span className="text-base font-bold text-slate-100 truncate min-w-0">
              {isStock ? (stockMeta?.name ?? id) : (coin?.name ?? id)}
            </span>
            <span className="text-sm text-slate-500 flex-shrink-0">
              {isStock ? tickerFromId(id) : coin?.symbol?.toUpperCase()}
            </span>
          </div>
          {/* Price + 24h badge — hidden on mobile (price is also shown on the chart);
              keeps the currency selector + theme toggle reachable on narrow screens. */}
          <div className="flex items-center gap-2 ml-1 max-md:hidden">
            <span className="text-base font-semibold text-slate-100">
              {currentPrice > 0 ? fmt(currentPrice) : '—'}
            </span>
            <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full ${
              change24hPos ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
            }`}>
              {change24hPos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {formatPercent(change24h)} 24h
            </span>
          </div>
        </div>

        {/* Right — shared global controls (same state as the /app header) */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <CurrencySelector />
          <ThemeToggle />
        </div>

      </div>

      {/* ── Main content ── */}
      <div className="px-8 py-8">
        <div className="space-y-8">

          {/* ── Top section: chart + holdings. Equal-height on desktop so the chart's
              bottom aligns with the Your Holdings panel (the tabs live full-width
              below this row). ── */}
          <div className="flex flex-col lg:flex-row gap-8 lg:items-stretch">

            {/* Left — chart (60–65%) */}
            <div className="flex-1 min-w-0 w-full flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-3 gap-y-2">
                {isStock ? (
                  /* Stocks: range buttons (line-only, no candle/line toggle) */
                  <div className="flex items-center gap-1">
                    {(['1M', '3M', '1Y'] as const).map((r) => (
                      <button
                        key={r}
                        onClick={() => setStockRange(r)}
                        className={`px-3 md:px-4 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                          stockRange === r
                            ? 'bg-slate-800 text-slate-100 border border-slate-600'
                            : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-1">
                      {INTERVALS.map(({ key, label }) => (
                        <button
                          key={key}
                          onClick={() => setCandleInterval(key)}
                          className={`px-3 md:px-4 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                            candleInterval === key
                              ? 'bg-slate-800 text-slate-100 border border-slate-600'
                              : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    {/* View toggle — moved out of the header, right-aligned above the chart */}
                    <div className="flex bg-slate-800 border border-slate-700 rounded-xl p-1 gap-1 flex-shrink-0">
                      <button
                        onClick={() => setView('line')}
                        title="Line chart"
                        className={`p-1.5 rounded-lg transition-all ${
                          view === 'line' ? 'bg-white text-[#0f0f0f]' : 'text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        <LineIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setView('candle')}
                        title="Candlestick chart"
                        className={`p-1.5 rounded-lg transition-all ${
                          view === 'candle' ? 'bg-white text-[#0f0f0f]' : 'text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        <CandleIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
              {/* Chart fills the column so its bottom aligns with the Your Holdings
                  panel; without a holdings panel it uses a comfortable fixed height.
                  Mobile keeps a fixed height for the stacked layout. TradingChart's
                  ResizeObserver picks up the height so candles/line/tooltips stay
                  correct at any size. */}
              <div className={holding ? 'w-full h-[360px] lg:h-auto lg:flex-1 lg:min-h-0' : 'w-full h-[360px] lg:h-[520px]'}>
                {isStock
                  ? <StockChart stockId={id} range={stockRange} />
                  : (
                    <TradingChart
                      coinId={id}
                      interval={candleInterval}
                      view={view}
                      priceLines={tierPriceLines}
                      // The header's own number: fmt(currentPrice) = currentPrice x rate.
                      livePrice={currentPrice > 0 ? currentPrice * rate : undefined}
                    />
                  )}
              </div>
            </div>

            {/* Right — holdings + wallet allocation (only when the user holds this
                coin). Market cap / supply / ATH-ATL / rank now live in the Coin's
                Info tab, so there's no standalone Token Information block anymore. */}
            {holding && (
              <div className="w-full lg:w-[38%] flex-shrink-0 flex flex-col gap-8">
                {/* Boxless on mobile (< md): a thin top divider replaces the card box,
                    matching the dashboard's boxless sections. Boxed at ≥ md. */}
                <div className="border-t border-slate-700/40 pt-5 md:border md:border-slate-700 md:bg-slate-800/40 md:rounded-xl md:p-5">
                  <p className="text-sm font-semibold text-slate-100 mb-4">Your Holdings</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[11px] text-slate-500 mb-0.5">Coins Held</p>
                      <p className="text-sm font-semibold text-slate-100">
                        {formatAmount(holding.amount)} <span className="text-[11px] font-normal text-slate-500">{holding.symbol}</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-slate-500 mb-0.5">Avg Buy Price</p>
                      <p className="text-sm font-semibold text-slate-100">{fmtDisplay(holding.display.avgBuyPrice)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-slate-500 mb-0.5">Total Value</p>
                      <p className="text-sm font-semibold text-slate-100">{fmt(holding.totalValue)}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-slate-500 mb-0.5">Allocation</p>
                      <p className="text-sm font-semibold text-slate-100">{holding.allocation.toFixed(1)}%</p>
                    </div>
                    <div className="col-span-2 pt-3 border-t border-slate-700/40">
                      <p className="text-[11px] text-slate-500 mb-0.5">All-Time Profit / Loss</p>
                      <div className="flex items-baseline gap-2">
                        <p className={`text-base font-bold font-display ${holding.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {holding.display.pnl >= 0 ? '+' : ''}{fmtDisplay(holding.display.pnl)}
                        </p>
                        <p className={`text-xs font-semibold ${holding.display.pnlPercent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          ({holding.display.pnlPercent >= 0 ? '+' : ''}{holding.display.pnlPercent.toFixed(1)}%)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Allocation by Wallet */}
                  {walletAllocations.length > 0 && (
                    <div className="mt-5 pt-5 border-t border-slate-700/40">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">
                        Allocation by Wallet
                      </p>

                      {/* Stacked bar */}
                      <div className="flex h-2 rounded-full overflow-hidden bg-slate-800 mb-3">
                        {walletAllocations.map((w) => (
                          <div
                            key={w.wallet}
                            style={{ width: `${w.percent}%`, backgroundColor: w.color }}
                            className="transition-all"
                            title={`${w.wallet} - ${w.percent.toFixed(1)}%`}
                          />
                        ))}
                      </div>

                      {/* Per-wallet rows */}
                      <div className="divide-y divide-slate-700/40">
                        {walletAllocations.map((w) => (
                          <div key={w.wallet} className="flex items-center justify-between py-2 gap-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: w.color }}
                              />
                              <span className="text-sm text-slate-200 truncate">{w.wallet}</span>
                              <span className="text-[11px] text-slate-500 flex-shrink-0">
                                {w.percent.toFixed(1)}%
                              </span>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm text-slate-100 font-medium">{fmt(w.valueUsd)}</p>
                              <p className="text-[11px] text-slate-500">
                                {fmtCoins(w.amount)} <span className="font-normal">{holding.symbol}</span>
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Total row */}
                      <div className="flex items-center justify-between pt-3 mt-1 border-t border-slate-700/60">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">
                          Total
                        </span>
                        <div className="text-right">
                          <p className="text-sm text-slate-100 font-semibold">{fmt(totalWalletValue)}</p>
                          <p className="text-[11px] text-slate-500">
                            {fmtCoins(totalWalletAmount)} <span className="font-normal">{holding.symbol}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Content tabs — Compare / Transaction History / Coin's Info. Full-width
              below the chart + holdings row (so the chart can shrink to the panel).
              Panels stay mounted (toggled via display) so Compare's pickers aren't
              reset when switching. */}
          <div>
            <div
              className="flex gap-1 md:overflow-x-auto"
              style={{ borderBottom: '0.5px solid var(--tab-row-border)' }}
            >
              {([
                // Compare is hidden for stocks (crypto/metal keep it, unchanged).
                ...(!isStock ? [{ id: 'compare', label: 'Compare' }] as const : []),
                { id: 'history', label: 'Transaction History' },
                // Info: crypto → CoinGecko metadata; stock → Yahoo quote panel.
                ...((isCrypto || isStock) ? [{ id: 'info', label: isStock ? 'Info' : "Coin's Info" }] as const : []),
              ] as const).map((tab) => {
                const isActive = contentTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setContentTab(tab.id)}
                    // Mobile: even thirds, centered, wrap the long label (no scroll).
                    // Desktop (≥ md): natural width, left-aligned, nowrap, 13px/14px.
                    className="flex-1 md:flex-none flex md:block items-center justify-center text-center md:text-left whitespace-normal md:whitespace-nowrap px-2 py-2 text-[11px] md:px-[14px] md:py-[10px] md:text-[13px] transition-colors"
                    style={{
                      background: 'transparent',
                      borderBottom: isActive ? '2px solid var(--tab-accent)' : '2px solid transparent',
                      marginBottom: '-0.5px',
                      color: isActive ? 'var(--tab-text-active)' : 'var(--tab-text-inactive)',
                      fontWeight: isActive ? 500 : 400,
                    }}
                    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--tab-text-hover)'; }}
                    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--tab-text-inactive)'; }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="pt-6">
              {!isStock && (
                <div style={{ display: contentTab === 'compare' ? 'block' : 'none' }}>
                  {coin && compareDefaultA
                    ? <AssetComparison defaultAssetA={compareDefaultA} holdings={holdings} />
                    : <p className="text-xs text-slate-500 py-2">Loading coin…</p>}
                </div>
              )}

              <div style={{ display: contentTab === 'history' ? 'block' : 'none' }}>
                <CoinTransactionHistory
                  transactions={coinTransactions}
                  symbol={coin?.symbol ?? (isStock ? tickerFromId(id) : id)}
                />
              </div>

              {isCrypto && (
                <div style={{ display: contentTab === 'info' ? 'block' : 'none' }}>
                  <CoinInfo
                    detail={detail}
                    loading={detailLoading}
                    name={coin?.name ?? id}
                    symbol={coin?.symbol ?? id}
                    fallbackImage={coin?.image}
                  />
                </div>
              )}

              {isStock && (
                <div style={{ display: contentTab === 'info' ? 'block' : 'none' }}>
                  <StockInfo
                    name={stockMeta?.name ?? id}
                    symbol={tickerFromId(id)}
                    type={getAssetType(id) === 'etf' ? 'etf' : 'stock'}
                    entry={stockEntry}
                    loading={stockLoading}
                  />
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}
